import { useState } from "react";
import {
  ActionList, ActionMenu, Button as PrimerButton, Checkbox, Dialog,
  FormControl, IconButton, Label, Select, Text, Textarea, TextInput,
  ToggleSwitch
} from "@primer/react";
import {
  KebabHorizontalIcon, PencilIcon, PlusIcon,
  SearchIcon, ShieldCheckIcon, TrashIcon, TriangleDownIcon
} from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetIdentity } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceStatus } from "../components/ComplianceStatus/ComplianceStatus.jsx";
import { ComplianceImpact, ComplianceMetric } from "../components/ComplianceMetric/ComplianceMetric.jsx";
import { Field } from "../components/Field/Field.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PolicyIdentity } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { PropertyValueIdentity } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { Section } from "../components/Section/Section.jsx";
import { ThemePicker } from "../components/ThemePicker/ThemePicker.jsx";
import "./StylesheetPage.css";

export function StylesheetPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const identityDocument = { properties: [{ id: "class", name: "Class", values: [{ id: "gold", name: "Gold" }] }] };
  return (
    <article className="stylesheet-page">
      <PageHeading description="A living reference for Regulator’s Primer interface and interaction language." actions={<Label>Development</Label>}>Stylesheet</PageHeading>

      <div className="stylesheet-sections">
        <Section heading="Domain identity">
          <div className="identity-stack">
            <AssetIdentity asset={{ id: "IGLN", name: "iShares Physical Gold" }} trailing={<ComplianceImpact impact={{ violations: 2, warnings: 1 }} />} />
            <PropertyValueIdentity property={identityDocument.properties[0]} value={identityDocument.properties[0].values[0]} />
            <PolicyIdentity document={identityDocument} policy={{ scope: { type: "specific", propertyId: "class", valueId: "gold" } }} />
          </div>
        </Section>

        <Section heading="Typography">
          <div className="type-specimen">
            <div><span>Page title</span><h2 className="text-page-title">Portfolio compliance</h2></div>
            <div><span>Section</span><h3 className="text-section-heading">Allocation rules</h3></div>
            <div><span>Identity</span><AssetIdentity asset={{ id: "IGLN", name: "iShares Physical Gold" }} /></div>
            <div><span>Body</span><p className="text-body">Clear, compact language keeps dense financial information readable.</p></div>
            <div><span>Supporting</span><p className="text-body muted-text">Explanations use the body size, with quieter color.</p></div>
            <div><span>Caption</span><Text className="text-caption muted-text">Last updated 2 minutes ago</Text></div>
          </div>
        </Section>

        <Section heading="Actions">
          <div className="component-row">
            <Button><PlusIcon /> Add property</Button>
            <Button tone="secondary" leadingVisual={PencilIcon} labelDetail="property">Edit</Button>
            <Button tone="quiet">Cancel</Button>
            <Button tone="danger" leadingVisual={TrashIcon} labelDetail="property">Delete</Button>
            <Button disabled>Disabled</Button>
            <IconButton icon={KebabHorizontalIcon} aria-label="More actions" />
            <ActionMenu>
              <ActionMenu.Button trailingVisual={TriangleDownIcon}>Actions</ActionMenu.Button>
              <ActionMenu.Overlay><ActionList><ActionList.Item onSelect={() => {}}><ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>Edit property</ActionList.Item><ActionList.Divider /><ActionList.Item variant="danger" onSelect={() => {}}><ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>Delete property</ActionList.Item></ActionList></ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </Section>

        <Section heading="Fields">
          <div className="field-grid">
            <Field label="Property name" help="Names are unique within the catalog."><TextInput defaultValue="Class" leadingVisual={SearchIcon} block /></Field>
            <Field label="Default value"><Select defaultValue="unset" block><Select.Option value="unset">Unset</Select.Option><Select.Option value="cash">Cash</Select.Option><Select.Option value="gold">Gold</Select.Option><Select.Option value="stocks">Stocks</Select.Option></Select></Field>
            <Field label="Policy note" error="Use a shorter description."><Textarea defaultValue="A deliberately long example policy note." resize="vertical" block /></Field>
          </div>
        </Section>

        <Section heading="Preferences">
          <div className="preference-list">
            <ThemePicker />
            <div className="preference"><span id="unset-assets-label"><strong>Include unset assets</strong><Text className="muted-text">Show incomplete classifications in reports.</Text></span><ToggleSwitch aria-labelledby="unset-assets-label" defaultChecked /></div>
            <FormControl layout="horizontal"><Checkbox defaultChecked /><FormControl.Label>Confirm destructive actions</FormControl.Label><FormControl.Caption>Ask before deleting policies or properties.</FormControl.Caption></FormControl>
          </div>
        </Section>

        <Section heading="Status">
          <div className="status-row"><ComplianceStatus status="compliant" /><ComplianceStatus status="warning" /><ComplianceStatus status="non-compliant" /><ComplianceStatus status="not-evaluated" /></div>
          <div className="status-row metric-row"><ComplianceMetric advisory={27} kind="gap" mandatory={30} size="large" /><ComplianceMetric advisory={1} kind="impact" mandatory={2} /><ComplianceMetric advisory={1} kind="impact" mandatory={0} /><ComplianceMetric advisory={0} kind="gap" mandatory={0} /></div>
          <div className="component-stack"><Alert heading="Information">This portfolio has not been checked yet.</Alert><Alert heading="Compliant" tone="success">All active rules are within range.</Alert><Alert heading="Warning" tone="warning">The Gold policy is approaching its limit.</Alert><Alert heading="Policy breach" tone="danger">The single-asset allocation is above 15%.</Alert></div>
        </Section>

        <Section heading="Domain composition" action={<Button tone="secondary"><PlusIcon /> Add rule</Button>}>
          <div className="rule-row"><span className="rule-icon"><ShieldCheckIcon /></span><div className="rule-description"><strong>Gold policy</strong><Text className="muted-text">Class is Gold</Text></div><dl><div><dt>Minimum</dt><dd>15%</dd></div><div><dt>Advisory</dt><dd>25%</dd></div><div><dt>Alert</dt><dd>30%</dd></div></dl><IconButton variant="invisible" icon={KebabHorizontalIcon} aria-label="Rule actions" /></div>
        </Section>

        <Section heading="Overlay">
          <PrimerButton onClick={() => setDialogOpen(true)}>Open dialog</PrimerButton>
          {dialogOpen ? <Dialog title="Add property" subtitle="Define the values assets can use." onClose={() => setDialogOpen(false)} footerButtons={[{ content: "Cancel", onClick: () => setDialogOpen(false) }, { content: "Add property", buttonType: "primary", onClick: () => setDialogOpen(false) }]}><FormControl><FormControl.Label>Property name</FormControl.Label><TextInput placeholder="e.g. Class" block /></FormControl></Dialog> : null}
        </Section>
      </div>
    </article>
  );
}
